import { createAttachmentDocumentTypeApi, updateAttachmentDocumentTypeApi } from "@/api/services/global/master";
import { TGetAttachmentDocumentTypeListOutputDto } from "@/api/services/global/master/dtos/attachment-document-type/get-master-attachment-document-type-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { SelectColor } from "@/components/elements/SelectColor";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { ATTACHED_DOCUMENT_COLOR, ATTACHED_DOCUMENT_COLOR_NAME } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataAttachmentDocumentTypeSchema } from "@/features/master-data/schema/masterDataAttachmentDocumentTypeSchema";
import { TMasterAttachmentDocumentTypeForm } from "@/features/master-data/types/masterDataAttachmentDocumentTypeForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TAttachmentDocumentTypeFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedAttachmentDocumentType?: TGetAttachmentDocumentTypeListOutputDto;
};

export const AttachmentDocumentTypeFormModal: FC<TAttachmentDocumentTypeFormModalProps> = ({ onClose, isOpen, selectedAttachmentDocumentType }) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      documentTypeName: selectedAttachmentDocumentType ? selectedAttachmentDocumentType.documentTypeName : undefined,
      color: selectedAttachmentDocumentType ? selectedAttachmentDocumentType.color : undefined,
    }),
    [selectedAttachmentDocumentType],
  );

  const methods = useForm<TMasterAttachmentDocumentTypeForm>({
    resolver: yupResolver(masterDataAttachmentDocumentTypeSchema),
    mode: "onBlur",
    defaultValues,
  });

  const { control, watch } = methods;

  const documentTypeName = watch("documentTypeName");

  const onSubmit = async (data: TMasterAttachmentDocumentTypeForm) => {
    try {
      if (selectedAttachmentDocumentType) {
        const rs = await updateAttachmentDocumentTypeApi({
          attachmentDocumentTypes: [
            {
              id: toNumber(selectedAttachmentDocumentType.id),
              documentTypeName: data.documentTypeName,
              color: data.color,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createAttachmentDocumentTypeApi({
          documentTypeName: data.documentTypeName,
          color: data.color,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_ATTACHMENT_DOCUMENT_TYPE] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const getAttachmentDocumentColorOptions = (label: string) => {
    return Object.entries(ATTACHED_DOCUMENT_COLOR_NAME).map(([key, value]) => ({
      label: label,
      value: value,
    }));
  };

  return (
    <Modal
      title={selectedAttachmentDocumentType ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={onClose}
      enableCloseIcon={false}
      width={420}
      height={392}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataAttachmentDocumentTypeSchema}>
        <Stack gap={2} mt={3}>
          <Grid item xs={6}>
            <FormField control={control} name="documentTypeName">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6} mt={1}>
            <FormField control={control} name="color">
              <SelectColor
                options={getAttachmentDocumentColorOptions(documentTypeName)}
                optionColors={ATTACHED_DOCUMENT_COLOR}
                textSelected={documentTypeName}
              />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between" gap="24px" mt={3}>
          <Button size="sm" variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button size="sm" type="submit">
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
